import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
	Badge,
	Box,
	Button,
	Group,
	NumberInput,
	Paper,
	Progress,
	Select,
	SimpleGrid,
	Text,
	Title,
} from '@mantine/core'
import {
	createOrgStripeSetupSession,
	createOrgStripePortalSession,
	getOrgBillingDashboard,
	syncOrgStripeSetupSession,
} from '../services/actions/orgActions'

const monthOptions = [
	{ value: '1', label: 'January' },
	{ value: '2', label: 'February' },
	{ value: '3', label: 'March' },
	{ value: '4', label: 'April' },
	{ value: '5', label: 'May' },
	{ value: '6', label: 'June' },
	{ value: '7', label: 'July' },
	{ value: '8', label: 'August' },
	{ value: '9', label: 'September' },
	{ value: '10', label: 'October' },
	{ value: '11', label: 'November' },
	{ value: '12', label: 'December' },
]

const formatMoney = (value, { min = 2, max = 2 } = {}) => {
	if (value === null || value === undefined || value === '') return '-'
	const numeric = Number(value)
	if (!Number.isFinite(numeric)) return '-'
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: min,
		maximumFractionDigits: max,
	}).format(numeric)
}

const formatMonthYear = (year, month) => {
	const monthName = monthOptions.find((item) => item.value === String(month))?.label || month
	return `${monthName} ${year}`
}

const AdminBillingPage = () => {
	const { orgSlug } = useParams()
	const location = useLocation()
	const navigate = useNavigate()
	const now = useMemo(() => new Date(), [])
	const [year, setYear] = useState(now.getFullYear())
	const [month, setMonth] = useState(String(now.getMonth() + 1))

	const [dashboard, setDashboard] = useState(null)
	const [loading, setLoading] = useState(true)
	const [refreshingUsage, setRefreshingUsage] = useState(false)
	const [startingStripeSetup, setStartingStripeSetup] = useState(false)
	const [syncingStripeSetup, setSyncingStripeSetup] = useState(false)
	const [openingStripePortal, setOpeningStripePortal] = useState(false)
	const [error, setError] = useState('')
	const [notice, setNotice] = useState('')

	const refreshUsage = useCallback(
		async (requestedYear, requestedMonth, { showLoader = false } = {}) => {
			if (showLoader) setLoading(true)
			setRefreshingUsage(!showLoader)
			setError('')
			try {
				const data = await getOrgBillingDashboard(orgSlug, {
					year: requestedYear,
					month: Number(requestedMonth),
				})
				setDashboard(data)
			} catch (err) {
				console.error('Failed to load billing dashboard:', err)
				setError(err.message || 'Failed to load billing dashboard.')
			} finally {
				setLoading(false)
				setRefreshingUsage(false)
			}
		},
		[orgSlug],
	)

	useEffect(() => {
		refreshUsage(year, month, { showLoader: true })
	}, [month, refreshUsage, year])

	useEffect(() => {
		const params = new URLSearchParams(location.search)
		const stripeSetupStatus = params.get('stripeSetup')
		const sessionId = params.get('session_id')

		if (!stripeSetupStatus) return

		if (stripeSetupStatus === 'canceled') {
			setNotice('Payment method setup was canceled.')
			params.delete('stripeSetup')
			params.delete('session_id')
			const nextSearch = params.toString()
			navigate(
				{
					pathname: location.pathname,
					search: nextSearch ? `?${nextSearch}` : '',
				},
				{ replace: true },
			)
			return
		}

		if (stripeSetupStatus !== 'success' || !sessionId) return

		let cancelled = false
		const syncSession = async () => {
			setSyncingStripeSetup(true)
			setError('')
			try {
				const result = await syncOrgStripeSetupSession(orgSlug, sessionId)
				if (cancelled) return
				await refreshUsage(year, month, { showLoader: false })
				const syncAction = result?.subscriptionSync?.action
				const warning = String(result?.warning || '').trim()
				const syncMessage =
					syncAction && syncAction !== 'subscription_updated' && syncAction !== 'subscription_created'
						? ` Stripe sync: ${syncAction}.`
						: ''
				setNotice(
					warning ||
						`Payment method added and synced.${syncMessage}`,
				)
			} catch (err) {
				if (cancelled) return
				console.error('Failed to sync Stripe setup session:', err)
				setError(err.message || 'Failed to sync Stripe setup session.')
			} finally {
				if (!cancelled) {
					setSyncingStripeSetup(false)
					params.delete('stripeSetup')
					params.delete('session_id')
					const nextSearch = params.toString()
					navigate(
						{
							pathname: location.pathname,
							search: nextSearch ? `?${nextSearch}` : '',
						},
						{ replace: true },
					)
				}
			}
		}

		syncSession()
		return () => {
			cancelled = true
		}
	}, [location.pathname, location.search, month, navigate, orgSlug, refreshUsage, year])

	const handleStartStripeSetup = async () => {
		setStartingStripeSetup(true)
		setError('')
		setNotice('')
		try {
			const result = await createOrgStripeSetupSession(orgSlug, {
				successUrl: `${window.location.origin}${window.location.pathname}?stripeSetup=success&session_id={CHECKOUT_SESSION_ID}`,
				cancelUrl: `${window.location.origin}${window.location.pathname}?stripeSetup=canceled`,
				setAsDefault: true,
			})
			const checkoutUrl = result?.session?.url
			if (!checkoutUrl) {
				throw new Error('Stripe did not return a setup checkout URL.')
			}
			window.location.assign(checkoutUrl)
		} catch (err) {
			console.error('Failed to create Stripe setup session:', err)
			setError(err.message || 'Failed to start Stripe payment method setup.')
			setStartingStripeSetup(false)
		}
	}

	const handleOpenStripePortal = async () => {
		setOpeningStripePortal(true)
		setError('')
		setNotice('')
		try {
			const result = await createOrgStripePortalSession(orgSlug, {
				returnUrl: window.location.href,
			})
			const portalUrl = result?.session?.url
			if (!portalUrl) {
				throw new Error('Stripe did not return a billing portal URL.')
			}
			window.location.assign(portalUrl)
		} catch (err) {
			console.error('Failed to open Stripe billing portal:', err)
			setError(err.message || 'Failed to open Stripe billing portal.')
			setOpeningStripePortal(false)
		}
	}

	if (loading) {
		return (
			<Box px='md' py='md'>
				Loading plan and billing dashboard...
			</Box>
		)
	}

	const usage = dashboard?.usagePreview?.usage || {}
	const plan = dashboard?.effectiveConfig?.plan || {}
	const userLimit = dashboard?.planLimits?.canAddUser || {}
	const adminLimit = dashboard?.planLimits?.canAddAdmin || {}
	const periodLabel = formatMonthYear(year, month)

	const includedCheckIns = Number(usage?.includedCheckIns ?? 0)
	const usedCheckIns = Number(usage?.totalBillableCheckIns ?? 0)
	const usageOnlyCharges = Number(dashboard?.upcomingInvoicePreview?.usageOnlyEstimate || 0)
	const nextCycleBase = Number(dashboard?.upcomingInvoicePreview?.nextCycleBaseEstimate || 0)
	const upcomingInvoiceEstimate = Number(dashboard?.upcomingInvoicePreview?.totalEstimate || 0)
	const checkInProgressPercent =
		includedCheckIns > 0 ? Math.min(100, Math.round((usedCheckIns / includedCheckIns) * 100)) : 0
	const currentUsers = Number(userLimit?.currentUsers ?? 0)
	const maxUsers = userLimit?.maxUsers
	const currentAdmins = Number(adminLimit?.currentAdmins ?? 0)
	const maxAdmins = adminLimit?.maxAdmins
	const userUsagePercent =
		Number.isInteger(maxUsers) && maxUsers > 0
			? Math.min(100, Math.round((currentUsers / maxUsers) * 100))
			: 0
	const adminUsagePercent =
		Number.isInteger(maxAdmins) && maxAdmins > 0
			? Math.min(100, Math.round((currentAdmins / maxAdmins) * 100))
			: 0

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='xs'>
				Plan & Billing
			</Title>
			<Text c='dimmed' mb='md'>
				{dashboard?.org?.name || orgSlug}
			</Text>

			{error && (
				<Text c='red' mb='sm'>
					{error}
				</Text>
			)}
			{notice && (
				<Text c='teal' mb='sm'>
					{notice}
				</Text>
			)}

			<Paper withBorder p='md' mb='md' radius='md'>
				<Group position='apart' align='end' mb='md'>
					<div>
						<Title order={4}>Current Plan & Usage</Title>
						<Text size='sm' c='dimmed'>
							Preview for {periodLabel}
						</Text>
					</div>
					<Group align='end'>
						<NumberInput
							label='Year'
							value={year}
							onChange={(value) => setYear(Number(value) || now.getFullYear())}
							min={2020}
							max={9999}
							w={120}
						/>
						<Select
							label='Month'
							value={month}
							onChange={(value) => setMonth(value || String(now.getMonth() + 1))}
							data={monthOptions}
							w={160}
						/>
						<Button
							variant='light'
							onClick={() => refreshUsage(year, month)}
							loading={refreshingUsage}
						>
							Refresh
						</Button>
					</Group>
				</Group>

				<SimpleGrid
					cols={5}
					breakpoints={[
						{ maxWidth: 'lg', cols: 2 },
						{ maxWidth: 'sm', cols: 1 },
					]}
				>
					<Paper withBorder p='md' radius='md'>
						<Group position='apart'>
							<Text fw={600}>Plan</Text>
							<Badge variant='light'>
								{plan?.name || 'Unassigned'}
							</Badge>
						</Group>
						<Text mt='sm' size='sm' c='dimmed'>
							Code: {plan?.code || '-'}
						</Text>
						<Text mt='xs' fw={700}>
							{formatMoney(dashboard?.effectiveConfig?.baseMonthlyPrice)}
							<Text span size='sm' c='dimmed'>
								{' '}
								/ month
							</Text>
						</Text>
					</Paper>

					<Paper withBorder p='md' radius='md'>
						<Text fw={600}>Check-In Usage</Text>
						<Text mt='sm'>
							{usage?.totalBillableCheckIns ?? 0} total
						</Text>
						<Text size='sm' c='dimmed'>
							Included: {includedCheckIns}
						</Text>
						<Progress value={checkInProgressPercent} mt='sm' radius='xl' />
						<Text size='sm' mt='sm'>
							Charges: {formatMoney(usage?.checkInCharges)}
						</Text>
					</Paper>

					<Paper withBorder p='md' radius='md'>
						<Text fw={600}>Message Usage</Text>
						<Text mt='sm'>
							SMS: {usage?.totalBillableSms ?? 0}
						</Text>
						<Text size='sm' c='dimmed'>
							Charges: {formatMoney(usage?.smsCharges)}
						</Text>
						<Text mt='sm'>
							MMS: {usage?.totalBillableMms ?? 0}
						</Text>
						<Text size='sm' c='dimmed'>
							Charges: {formatMoney(usage?.mmsCharges)}
						</Text>
						<Text mt='sm'>
							Emails: {usage?.totalBillableEmails ?? 0}
						</Text>
						<Text size='sm' c='dimmed'>
							Charges: {formatMoney(usage?.emailCharges)}
						</Text>
					</Paper>

					<Paper withBorder p='md' radius='md'>
						<Text fw={600}>Upcoming Renewal Estimate</Text>
						<Text mt='sm' size='sm' c='dimmed'>
							Next cycle base
						</Text>
						<Text fw={700}>{formatMoney(nextCycleBase)}</Text>
						<Text mt='sm' size='sm' c='dimmed'>
							Current cycle usage
						</Text>
						<Text fw={700}>{formatMoney(usageOnlyCharges)}</Text>
						<Text mt='sm' size='sm' c='dimmed'>
							Estimated total
						</Text>
						<Text fw={700}>{formatMoney(upcomingInvoiceEstimate)}</Text>
					</Paper>

					<Paper withBorder p='md' radius='md'>
						<Text fw={600}>User/Admin Usage</Text>
						<Text mt='sm' size='sm'>
							Users: {currentUsers} / {maxUsers ?? 'Unlimited'}
						</Text>
						{Number.isInteger(maxUsers) && maxUsers > 0 && (
							<Progress value={userUsagePercent} mt={6} radius='xl' />
						)}
						<Text mt='sm' size='sm'>
							Admins: {currentAdmins} / {maxAdmins ?? 'Unlimited'}
						</Text>
						{Number.isInteger(maxAdmins) && maxAdmins > 0 && (
							<Progress value={adminUsagePercent} mt={6} radius='xl' />
						)}
						<Group mt='sm' spacing='xs'>
							<Badge
								color={userLimit?.allowed === false ? 'red' : 'teal'}
								variant='light'
							>
								{userLimit?.allowed === false ? 'User limit reached' : 'Users in range'}
							</Badge>
							<Badge
								color={adminLimit?.allowed === false ? 'red' : 'teal'}
								variant='light'
							>
								{adminLimit?.allowed === false ? 'Admin limit reached' : 'Admins in range'}
							</Badge>
						</Group>
						{(userLimit?.allowed === false || adminLimit?.allowed === false) && (
							<Text mt='sm' size='xs' c='red'>
								At least one license limit is reached. Update your plan to add more seats.
							</Text>
						)}
					</Paper>
				</SimpleGrid>
			</Paper>

			<Paper withBorder p='md' radius='md'>
				<Group position='apart'>
					<div>
						<Title order={4}>Billing Management</Title>
						<Text size='sm' c='dimmed'>
							Payment methods, subscriptions, invoices, and receipts are managed in Stripe.
						</Text>
					</div>
					<Group>
						<Button
							variant='light'
							onClick={handleStartStripeSetup}
							loading={startingStripeSetup || syncingStripeSetup}
						>
							Add / Update Card
						</Button>
						<Button
							onClick={handleOpenStripePortal}
							loading={openingStripePortal}
							disabled={syncingStripeSetup}
						>
							Open Stripe Portal
						</Button>
					</Group>
				</Group>
			</Paper>
		</Box>
	)
}

export default AdminBillingPage
